/**
 * Category Application Service
 *
 * Coordinates category-related business operations.
 * Depends on CategoryRepository for data persistence.
 * Pure TypeScript - no React dependencies.
 */

import { Category } from "../../domain/entities/Category";
import { ICategoryRepository } from "../../domain/repositories/ICategory.repository";

export class CategoryService {
  constructor(private categoryRepository: ICategoryRepository) {}

  /**
   * Get all categories
   */
  async getAllCategories(): Promise<Category[]> {
    return this.categoryRepository.findAll();
  }

  /**
   * Get a single category
   */
  async getCategoryById(id: string): Promise<Category | null> {
    return this.categoryRepository.findById(id);
  }

  /**
   * Get children of a category
   */
  async getChildren(parentId: string | null): Promise<Category[]> {
    return this.categoryRepository.findByParentId(parentId);
  }

  /**
   * Get all descendants of a category (including children, grandchildren, etc.)
   */
  async getDescendants(categoryId: string): Promise<Category[]> {
    return this.categoryRepository.findDescendants(categoryId);
  }

  /**
   * Get all descendant IDs recursively
   */
  async getDescendantIds(categoryId: string): Promise<string[]> {
    const descendants = await this.getDescendants(categoryId);
    return [categoryId, ...descendants.map((d) => d.id)];
  }

  /**
   * Create a new category
   */
  async createCategory(
    name: string,
    parentId: string | null,
    level: number,
  ): Promise<Category> {
    // Validation
    if (!name?.trim()) {
      throw new Error("Category name is required");
    }

    // Verify parent exists if specified
    if (parentId) {
      const parent = await this.categoryRepository.findById(parentId);
      if (!parent) {
        throw new Error(`Parent category with ID ${parentId} not found`);
      }
      // Verify level consistency
      if (parent.level + 1 !== level) {
        throw new Error(
          `Level mismatch: parent is level ${parent.level}, expected child level ${parent.level + 1}`,
        );
      }
    }

    return this.categoryRepository.create(name, parentId, level);
  }

  /**
   * Update a category name
   */
  async updateCategory(id: string, name: string): Promise<Category> {
    // Validation
    if (!name?.trim()) {
      throw new Error("Category name is required");
    }

    // Verify category exists
    const existing = await this.categoryRepository.findById(id);
    if (!existing) {
      throw new Error(`Category with ID ${id} not found`);
    }

    return this.categoryRepository.update(id, name);
  }

  /**
   * Delete a category
   * Note: Business logic may enforce constraints (e.g., no products in category)
   */
  async deleteCategory(id: string): Promise<void> {
    // Verify category exists
    const existing = await this.categoryRepository.findById(id);
    if (!existing) {
      throw new Error(`Category with ID ${id} not found`);
    }

    // Check if category has children
    const children = await this.categoryRepository.findByParentId(id);
    if (children.length > 0) {
      throw new Error(
        `Cannot delete category with ID ${id}: it has ${children.length} subcategories`,
      );
    }

    // TODO: In Phase 2, add check: category should not have products

    return this.categoryRepository.delete(id);
  }

  /**
   * Get category tree structure
   */
  async getCategoryTree(): Promise<Category[]> {
    return this.categoryRepository.getTree();
  }

  /**
   * Check if a category is a descendant of another
   */
  async isDescendantOf(
    categoryId: string,
    potentialParentId: string,
  ): Promise<boolean> {
    if (categoryId === potentialParentId) {
      return true; // A category is a "descendant" of itself (or consider false - depends on use case)
    }

    const descendants =
      await this.categoryRepository.findDescendants(potentialParentId);
    return descendants.some((d) => d.id === categoryId);
  }

  /**
   * Get the full path of a category (all ancestors up to root)
   */
  async getCategoryPath(categoryId: string): Promise<Category[]> {
    const path: Category[] = [];
    let current = await this.categoryRepository.findById(categoryId);

    while (current) {
      path.unshift(current);
      if (current.parentId) {
        current = await this.categoryRepository.findById(current.parentId);
      } else {
        break;
      }
    }

    return path;
  }
}
